import React, {useEffect, useRef, useState} from 'react';
import {FilePreviewOriginalWrapper, StyledFilePreviewInfo} from "./FilePreviewOriginal.styled";
import {LocalDocumentFileFormat} from "../../../../../models/local-documents.models";
import {useTranslation} from "react-i18next";
import {PDFContainer, PDFEmbed} from "../../../../../components/ocr-batch/PDFViewer/PDFViewer.styled";
import ImageViewer from "../../../../../components/ocr-batch/ImageViewer";
import {Button, CircularProgress, Typography} from "@mui/material";

function downloadBinaryAsFile(
    data: ArrayBuffer | Uint8Array,
    fileName: string,
    mimeType = "application/octet-stream",
) {
    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
}

type FileFormat = 'PDF' | 'JPEG' | 'PNG' | 'JPG';

const FILE_FORMAT_TO_MIME_TYPE: Record<FileFormat, string> = {
    PDF: 'application/pdf',
    JPEG: 'image/jpeg',
    JPG: 'image/jpeg',
    PNG: 'image/png',
};

const uint8ArrayToUrl = (data: Uint8Array): string => {
    const blob = new Blob([data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
};

interface FilePreviewOriginalProps {
    fileName: string;
    contentType: LocalDocumentFileFormat;
    baseContent: Uint8Array;
}

const FilePreviewOriginal = ({fileName, contentType, baseContent}: FilePreviewOriginalProps) => {
    const [error, setError]   = useState<string | null>(null);
    const [loadError, setLoadError] = useState<boolean>(false);

    const {t} = useTranslation();

    const [url, setUrl] = useState<string>('');

    useEffect(() => {
        const availableFormats = ['PDF', 'PNG', 'JPEG', 'JPG'];

        if (availableFormats.includes(contentType) && baseContent.length > 0) {
            setLoadError(false);
            setError(null);
        }
        else {
            setLoadError(true);
            setError(t('digitizationDetails.messages.filePreviewUnavailable'));
        }
    }, [baseContent, contentType, t]);
    
    useEffect(() => {
        const objectUrl = uint8ArrayToUrl(baseContent);
        setUrl(objectUrl);

        return () => URL.revokeObjectURL(objectUrl); // cleanup — zapobiega memory leak
    }, [baseContent]);

    const handleDownload = () => {
        downloadBinaryAsFile(baseContent, fileName, contentType);
    };

    return (
        <FilePreviewOriginalWrapper>
            {!baseContent.length && (
                <StyledFilePreviewInfo>
                    <CircularProgress />
                </StyledFilePreviewInfo>
            )}

            {(error && !loadError) && (
                <StyledFilePreviewInfo>
                    {t('digitizationDetails.messages.fileLoadingError')}
                </StyledFilePreviewInfo>
            )}

            {loadError && (
                <StyledFilePreviewInfo>
                    <Typography>
                        {t('digitizationDetails.messages.filePreviewUnavailable')}
                    </Typography>

                    <Button
                        onClick={handleDownload}
                        sx={{
                            padding: '4px 16px',
                            maxWidth: '100%',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                        }}
                    >
                        {t('digitizationDetails.tools.downloadButton', {fileName: fileName})}
                    </Button>
                </StyledFilePreviewInfo>
            )}

            {(['PNG', 'JPEG', 'JPG'].includes(contentType)) && (
                <ImageViewer
                    mimeType={FILE_FORMAT_TO_MIME_TYPE[contentType as FileFormat]}
                    data={baseContent}
                />
            )}

            {contentType === 'PDF' && (
                <PDFContainer>
                    <PDFEmbed
                        src={url}
                        type={FILE_FORMAT_TO_MIME_TYPE[contentType]}
                        title={fileName}
                    />
                </PDFContainer>
                )}
        </FilePreviewOriginalWrapper>
    );
};

export default FilePreviewOriginal;